import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Filter, Settings } from 'lucide-react';
import Image from '../components/common/Image';
import Footer from '../components/sections/Footer';
import { useProjects } from '@/hooks/useProjects';
export default function Works() {
    const [selectedCategory, setSelectedCategory] = useState('すべて');
    const { projects, loading, error } = useProjects();
    const categories = useMemo(() => [
        'すべて',
        ...Array.from(new Set(projects.map((p) => p.category))),
    ], [projects]);
    const filteredProjects = selectedCategory === 'すべて'
        ? projects
        : projects.filter((project) => project.category === selectedCategory);
    if (loading) {
        return (<main className='min-h-screen flex items-center justify-center'>
        <p className='text-navy'>読み込み中...</p>
      </main>);
    }
    if (error) {
        return (<main className='min-h-screen flex items-center justify-center'>
        <p className='text-red-600'>{error}</p>
      </main>);
    }
    return (<main className='min-h-screen'>
      {/* Hero Section */}
      <section className='relative h-[70vh] min-h-[600px] overflow-hidden'>
        <div className='absolute inset-0'>
          <Image src='/images/works/works-hero.webp' alt='施工実績' fill className='object-cover' priority/>
          <div className='absolute inset-0 bg-gradient-to-b from-navy/40 via-navy/30 to-navy/60'/>
        </div>

        <div className='relative z-10 h-full flex flex-col justify-center px-6 md:px-12 lg:px-24'>
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <h1 className='text-7xl md:text-8xl text-white font-bebas tracking-wider'>
              OUR WORKS
            </h1>
            <p className='mt-6 text-xl md:text-2xl text-white font-noto'>
              施工実績
            </p>
            <p className='mt-4 text-lg text-white/80 max-w-2xl leading-relaxed'>
              電気設備、空調設備、太陽光発電など、これまでに手がけた主な施工実績をご紹介します。
              岩手県を中心に、東北一円で数多くのプロジェクトを手がけてきました。
            </p>
          </motion.div>
        </div>
      </section>

      {/* Projects Section */}
      <section className='bg-white py-24'>
        <div className='container mx-auto px-6'>
          {/* Category Filter */}
          <div className='mb-12'>
            <div className='flex items-center justify-between mb-6'>
              <div className='flex items-center gap-2'>
                <Filter size={20} className='text-navy'/>
                <h2 className='text-lg font-medium text-navy'>
                  カテゴリーで絞り込む
                </h2>
              </div>

              {/* Admin Button */}
              <Link to='/admin/login' className='inline-flex items-center px-4 py-2 bg-navy/10 backdrop-blur-sm text-navy rounded-full text-sm font-medium transition-all hover:bg-navy/20 border border-navy/20'>
                <Settings className='w-4 h-4 mr-2'/>
                管理者ログイン
              </Link>
            </div>

            <div className='flex flex-wrap gap-3'>
              {categories.map((category) => (<button key={category} onClick={() => setSelectedCategory(category)} className={`px-6 py-2 rounded-full text-sm font-medium transition-all
                    ${selectedCategory === category
                ? 'bg-navy text-white'
                : 'bg-lightgray text-navy/70 hover:bg-navy/10'}`}>
                  {category}
                </button>))}
            </div>
          </div>

          {/* Projects Grid */}
          <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
            {filteredProjects.map((project, index) => (<motion.div key={project.id} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} transition={{
                duration: 0.5,
                delay: index * 0.1,
                ease: 'easeOut',
            }} viewport={{ once: true }}>
                <Link to={`/works/${project.slug}`} className='block group bg-lightgray rounded-lg overflow-hidden hover:shadow-lg transition-shadow duration-300'>
                  <div className='relative aspect-[4/3] overflow-hidden'>
                    <Image src={project.image_url} alt={project.title} fill className='object-cover transition-transform duration-500 group-hover:scale-105' sizes='(max-width: 768px) 100vw, (max-width: 1024px) 50vw, 33vw'/>
                    <div className='absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300'/>
                  </div>

                  <div className='p-8'>
                    <div className='flex items-center gap-3 mb-4'>
                      <span className='px-3 py-1 bg-navy/10 text-navy text-sm rounded-full'>
                        {project.category}
                      </span>
                      <span className='text-sm text-navy/60'>
                        {project.location}
                      </span>
                    </div>

                    <h3 className='text-xl font-medium text-navy group-hover:text-skyblue transition-colors'>
                      {project.title}
                    </h3>

                    <p className='mt-4 text-navy/70 line-clamp-2'>
                      {project.description || '詳細は準備中です。'}
                    </p>
                  </div>
                </Link>
              </motion.div>))}
          </div>
        </div>
      </section>

      <Footer />
    </main>);
}
